/**
 * Maps API question types to rule UI kinds for Loja Perfeita (dependency + scoring).
 * API uses AnswerTypesEnum: boolean, choice, number, text, date, file.
 */

/** AnswerTypesEnum boolean subtypes — infer boolean rules when `type` is missing but subtype is present. */
const BOOLEAN_QUESTION_SUBTYPES = new Set([
    'before_supply',
    'out_of_stock',
    'near_due_date',
    'tabloid',
    'additional_point',
])

const NUMERIC_STYLE_OPERATORS = new Set([
    'greater_than',
    'greater_than_or_equal',
    'less_than',
    'less_than_or_equal',
])

export function normalizeChoiceList(raw) {
    if (!raw) return []
    const arr = Array.isArray(raw) ? raw : (raw.data && Array.isArray(raw.data) ? raw.data : [])
    return arr.map((c) => ({ id: c.id, label: c.label || `#${c.id}` }))
}

/**
 * @param {object} item - questionnaire-items API payload (with question + choices when loaded)
 */
export function extractQuestionMetaFromItem(item) {
    const q = item && item.question ? item.question : {}
    if (!q || typeof q !== 'object') {
        return { question_type: null, question_subtype: null, question_is_multiple: false, choice_options: [] }
    }
    const choices = normalizeChoiceList(item.choices)
    return {
        question_type: q.type || null,
        question_subtype: q.subtype || null,
        question_is_multiple: Boolean(q.is_multiple),
        choice_options: choices,
    }
}

/**
 * @param {string|null|undefined} questionType
 * @param {boolean} isMultiple
 * @param {string|null|undefined} [questionSubtype] - AnswerTypesEnum subtype (e.g. out_of_stock)
 */
export function getQuestionKind(questionType, isMultiple, questionSubtype = null) {
    if (!questionType && questionSubtype && BOOLEAN_QUESTION_SUBTYPES.has(String(questionSubtype))) {
        return 'boolean'
    }
    if (!questionType) return 'unknown'
    if (questionType === 'boolean') return 'boolean'
    if (questionType === 'number') return 'number'
    if (questionType === 'choice') return isMultiple ? 'choice_multi' : 'choice_single'
    if (questionType === 'text') return 'text'
    if (questionType === 'date') return 'date'
    if (questionType === 'file') return 'file'
    return 'unknown'
}

export function operatorsForKind(kind) {
    const numeric = ['equals', 'not_equals', 'greater_than', 'greater_than_or_equal', 'less_than', 'less_than_or_equal']
    const eq = ['equals', 'not_equals']
    if (kind === 'boolean') return eq
    if (kind === 'number') return numeric
    if (kind === 'choice_single') return eq
    if (kind === 'choice_multi') {
        return ['includes', 'not_includes', 'selection_count_gte', 'selection_count_lte', 'selection_count_equals']
    }
    if (kind === 'text') return [...eq, 'contains']
    if (kind === 'date') return numeric
    if (kind === 'file') return numeric
    return numeric
}

export function defaultOperatorForKind(kind) {
    if (kind === 'boolean' || kind === 'choice_single' || kind === 'text' || kind === 'date') {
        return 'equals'
    }
    if (kind === 'number' || kind === 'file') return 'greater_than_or_equal'
    if (kind === 'choice_multi') return 'includes'
    return 'equals'
}

export function defaultValueForKind(kind) {
    if (kind === 'boolean') return '1'
    if (kind === 'number') return '0'
    if (kind === 'file') return '1'
    if (kind === 'choice_single' || kind === 'choice_multi') return ''
    if (kind === 'text') return ''
    if (kind === 'date') return ''
    return ''
}

export function isSelectionCountOperator(op) {
    return op === 'selection_count_gte' || op === 'selection_count_lte' || op === 'selection_count_equals'
}

function displayForBinaryStoredValue(rawTrimmed, translate) {
    const t = typeof translate === 'function' ? translate : () => ''
    const lower = rawTrimmed.toLowerCase()
    if (rawTrimmed === '1' || lower === 'true') {
        return t('BOOLEAN_VALUES.yes') || '—'
    }
    if (rawTrimmed === '0' || lower === 'false') {
        return t('BOOLEAN_VALUES.no') || '—'
    }
    return null
}

function isEmptyDisplayValue(rawTrimmed) {
    return rawTrimmed === ''
}

/**
 * Human-readable condition_value for lists and summaries (same rules as the question modal).
 *
 * @param {string} kind - from getQuestionKind
 * @param {string} [operator] - condition_operator
 * @param {unknown} value - stored condition_value
 * @param {Array<{id: *, label: string}>} [choiceOptions]
 * @param {(i18nSuffix: string) => string} translate - e.g. (s) => getI18n('LOJA_PERFEITA', s); used for BOOLEAN_VALUES.yes / BOOLEAN_VALUES.no
 * @returns {string}
 */
export function formatConditionValueForDisplay(kind, operator, value, choiceOptions, translate) {
    const raw = value != null ? String(value).trim() : ''

    if (kind === 'boolean') {
        const tBool = typeof translate === 'function' ? translate : () => ''
        if (isEmptyDisplayValue(raw)) {
            return tBool('BOOLEAN_VALUES.no') || '—'
        }
        const bin = displayForBinaryStoredValue(raw, translate)
        return bin != null ? bin : (tBool('BOOLEAN_VALUES.no') || '—')
    }

    if (isSelectionCountOperator(operator)) {
        return isEmptyDisplayValue(raw) ? '—' : raw
    }

    if (kind === 'choice_single' || (kind === 'choice_multi' && (operator === 'includes' || operator === 'not_includes'))) {
        const opt = (choiceOptions || []).find((c) => String(c.id) === String(value != null ? value : '').trim())
        if (opt) {
            const label = opt.label != null ? String(opt.label).trim() : ''
            return label !== '' ? label : '—'
        }
        const binary = displayForBinaryStoredValue(raw, translate)
        if (binary != null) return binary
        return isEmptyDisplayValue(raw) ? '—' : raw
    }

    const canInferBooleanForUnknown =
        kind === 'unknown' &&
        !NUMERIC_STYLE_OPERATORS.has(operator || '') &&
        !isSelectionCountOperator(operator)

    if (canInferBooleanForUnknown) {
        const binary = displayForBinaryStoredValue(raw, translate)
        if (binary != null) return binary
    }

    return isEmptyDisplayValue(raw) ? '—' : raw
}

/**
 * Max points a criterion task can award (engine: first matching scoring rule, else default weight).
 * @param {object} task - API row with weight and scoring_rules and/or scoringRules
 * @returns {number}
 */
export function effectiveCriterionTaskWeight(task) {
    if (!task || typeof task !== 'object') return 0
    const base = Number(task.weight) || 0
    const rawRules = task.scoring_rules != null ? task.scoring_rules : task.scoringRules
    const rules = Array.isArray(rawRules) ? rawRules : []
    let max = base
    for (const r of rules) {
        const w = Number(r && r.weight) || 0
        if (w > max) max = w
    }
    return max
}
